import React, { useEffect, useState } from "react";
import { Card, Container, Form, Accordion, Col, Row } from "react-bootstrap";
import ProjectDataTable from "../Table/ProjectDataTable";
import "./Project.css";
import { FaTable } from "react-icons/fa6";
import { BsGridFill, BsChevronLeft } from "react-icons/bs";
import moment from "moment";
import ProjectDataCard from "../Table/ProjectDataCard";

interface Project {
    projectTitle: string;
    projectId: string;
    daysLeft: number;

    sprurl: string;

    sD_NRROk: string;
    sD_NRROkB: string;
    sD_NRROkS: string;

    sD_CDSLay: string;
    sD_CDSLayB: string;
    sD_CDSLayS: string;

    sD_CDROk: string;
    sD_CDROkB: string;
    sD_CDROkS: string;

    sD_LDVOk: string;
    sD_LDVOkB: string;
    sD_LDVOkS: string;
}

interface Props {
    projects: Project[];
    progress: boolean;
    initial: boolean;
    onFilterActivePins: (activePins: any[]) => void;
}

const Project: React.FC<Props> = ({ projects, progress, initial, onFilterActivePins }) => {
    const [data, setData] = useState<Project[]>([]);
    const [viewMode, setViewMode] = useState<"table" | "cards">("table");

    // filters
    const [idQuery, setIdQuery] = useState("");
    const [nameQuery, setNameQuery] = useState("");
    const [activeTableFilters, setActiveTableFilters] = useState<{}>({});
    const [filtersActive, setFiltersActive] = useState<boolean>(false);
    const [previousFilters, setPreviousFilters] = useState<any>({});
    const [singleProjectSelected, setSingleProjectSelected] = useState<number | null>(null);

    const scrollToTop = () => {
        window.scrollTo({
            top: 0,
            behavior: "smooth"
        });
    }

    const selectSingleProject = (pin: number) => {
        setViewMode("cards");
        setPreviousFilters({ idQuery: idQuery, nameQuery: nameQuery, activeTableFilters: activeTableFilters });
        setFiltersActive(false)

        const selectedProject = projects.filter((project: any) => {
            return project.projectId === pin;
        })

        setData(selectedProject);
        setSingleProjectSelected(pin);

        scrollToTop();
    }

    const unselectSingleProject = () => {
        setIdQuery(previousFilters.idQuery);
        setNameQuery(previousFilters.nameQuery);
        setActiveTableFilters(previousFilters.activeTableFilters);
        setPreviousFilters({});
        applyFilters();
        setViewMode("table");
        setSingleProjectSelected(null);

    }

    const applyFilters = () => {
        const filteredData = projects.filter((project: any) => {
            // filter by project name
            if (
                nameQuery &&
                project.projectTitle &&
                !project.projectTitle.toLowerCase().includes(nameQuery.toLowerCase())
            ) {
                return false;
            }

            // filter by project id (pin)
            if (idQuery && !project.projectId.toString().startsWith(idQuery)) {
                return false;
            }

            // date/state-related filters
            return Object.values(activeTableFilters).every((filter: any) => {
                const projectDate = project[filter.keyName];
                if (!projectDate) return false;

                const projectDateISO = new Date(projectDate);
                const fromDate = filter.dateFrom ? new Date(filter.dateFrom) : null;
                const toDate = filter.dateTo ? new Date(filter.dateTo) : null;

                const isAfterFrom = fromDate ? projectDateISO >= fromDate : true;
                const isBeforeTo = toDate ? projectDateISO <= toDate : true;

                return isAfterFrom && isBeforeTo;
            });
        });

        setData(filteredData);
    };

    const handleTableFilterSubmit = (filterData: { keyName: string; dateFrom: string; dateTo: string; field: string }) => {
        const { field } = filterData;

        setActiveTableFilters((prevFilters) => {
            const updatedFilters = {
                ...prevFilters,
                [field]: filterData,
            };
            return updatedFilters;
        });

        scrollToTop();
    };

    const removeFilter = (table: string) => {
        setActiveTableFilters((prevFilters: any) => {
            const updatedFilters = { ...prevFilters };
            delete updatedFilters[table];
            return updatedFilters;
        });
    };


    const removeAllTableFilters = () => {
        Object.entries(activeTableFilters).map(([field, _]: any) => {
            removeFilter(field);
        })
    }

    const resetFilters = () => {
        setNameQuery("");
        setIdQuery("");
    };

    const handleNameChange = (query: string) => {
        setNameQuery(query);
    };

    const handleIdChange = (query: string) => {
        setIdQuery(query);
    };

    useEffect(() => {
        applyFilters();
    }, [nameQuery, idQuery, activeTableFilters]);

    useEffect(() => {
        if (Object.entries(activeTableFilters).length > 0) {
            setFiltersActive(true)
        } else {
            setFiltersActive(false)
        }
    }, [activeTableFilters])

    useEffect(() => {
        setData(projects || []);
    }, [projects]);

    useEffect(() => {

        if (!initial) {
            const activePins = Array.from(
                new Set(
                    data.map((project: any) => project.projectId)
                )
            );
            onFilterActivePins(activePins)
        }

    }, [data]);

    const toggleViewMode = () => {
        setViewMode((prevMode) => (prevMode === "table" ? "cards" : "table"));
    };

    if (initial) {
        return (

            <div className="initial-projects-ph">
                <h1>Welcome to <b>The Hub</b>!</h1>
                <p>To get started, please fill in the search parameters above and click the Search button to initiate a new search. You’ll be able to view all the projects and their related discussions you’re looking for.</p>
            </div>

        )
    }

    return (
        <Container fluid className="project-card" style={{ height: "1450px", overflowY: "auto" }}>
            {/* header and mode switch button */}
            <Card.Body>
                <Card.Title className="d-flex justify-content-between align-items-center mb-4">
                    <h2 className="font-weight-bold" style={{ color: "#11343A" }}>Projects Overview</h2>
                    {projects.length > 0 && singleProjectSelected === null && (
                        <button onClick={toggleViewMode} className="btn btn-primary btn-icon btn-switch">
                            {viewMode === "table" ? <BsGridFill /> : <FaTable />}
                        </button>
                    )}
                </Card.Title>

                {/* name and pin based projects filter */}
                {projects.length > 0 && (
                    <>
                        {singleProjectSelected !== null && (
                            <>

                                <div className="single-project-header" onClick={unselectSingleProject}>
                                    <BsChevronLeft />Back to all projects
                                </div>
                            </>
                        )}

                        {singleProjectSelected === null && (
                            <Row>
                                <Col className="col-12">
                                    <div className="filter-container">
                                        <Accordion className="mb-3">
                                            <Accordion.Item eventKey="0">
                                                <Accordion.Header>Filter Projects</Accordion.Header>
                                                <Accordion.Body>
                                                    <form className="filter-form-container">
                                                        <div className="filter-form">
                                                            {/* project name filter */}
                                                            <Form.Group className="filter-form-group">
                                                                <Form.Label htmlFor="filterName" className="filter-form-label">
                                                                    Filter by Project Name
                                                                </Form.Label>
                                                                <Form.Control
                                                                    className="rounded-pill"
                                                                    id="filterName"
                                                                    type="text"
                                                                    value={nameQuery}
                                                                    onChange={(e) => handleNameChange(e.currentTarget.value)}
                                                                />
                                                            </Form.Group>

                                                            {/* pin filter */}
                                                            <Form.Group className="filter-form-group">
                                                                <Form.Label htmlFor="filterId" className="filter-form-label">
                                                                    Filter by PIN
                                                                </Form.Label>
                                                                <Form.Control
                                                                    className="rounded-pill"
                                                                    id="filterId"
                                                                    type="text"
                                                                    value={idQuery}
                                                                    onChange={(e) => handleIdChange(e.currentTarget.value)}
                                                                />
                                                            </Form.Group>
                                                        </div>

                                                        {/* filters reset button */}
                                                        <div className="d-flex justify-content-center mt-4">
                                                            <button className="btn btn-primary" type="reset" onClick={resetFilters}>
                                                                Reset filters
                                                            </button>
                                                        </div>
                                                    </form>
                                                </Accordion.Body>
                                            </Accordion.Item>
                                        </Accordion>

                                        {/* active filters warning */}
                                        {Object.entries(activeTableFilters).length > 0 && (
                                            <div className="active-table-filters-container my-3 px-1">
                                                <div className="active-filters p-3">
                                                    <div className="row mb-4">
                                                        <div className="col-md-8">
                                                            <h5 style={{ fontSize: '1rem' }}>Active date range Filters</h5>
                                                        </div>
                                                        <div className="col-md-4 d-flex justify-content-end">
                                                            <button
                                                                className="btn btn-danger btn-sm"
                                                                onClick={() => removeAllTableFilters()}
                                                            >
                                                                Remove all active date range filters
                                                            </button>
                                                        </div>
                                                    </div>
                                                    {Object.entries(activeTableFilters).map(([field, filter]: any, index: number) => (
                                                        <div key={field} className="row" style={{
                                                            borderTop: index === 0 ? 'none' : '1px solid rgb(236, 225, 170)',
                                                            marginTop: index === 0 ? 'none' : '0.7rem',
                                                            paddingTop: index === 0 ? 'none' : '0.7rem',
                                                        }}>
                                                            {/* filter spec */}
                                                            <div className="col-md-8" >
                                                                <b>{field}</b> <small>between <b>{moment(filter.dateFrom).format('ll')}</b> and <b>{moment(filter.dateTo).format('ll')}</b></small>
                                                            </div>
                                                            {/* remove single filter button */}
                                                            <div className="col-md-4 d-flex justify-content-end">
                                                                <button
                                                                    onClick={() => removeFilter(field)}
                                                                    className="btn btn-danger btn-sm"
                                                                >
                                                                    Remove
                                                                </button>
                                                            </div>
                                                        </div>
                                                    ))}
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                </Col>
                            </Row>
                        )}

                    </>


                )}


                {/* data view */}
                {viewMode === "table" ? (
                    <ProjectDataTable
                        projects={data}
                        progress={progress}
                        filtersActive={filtersActive}
                        activeTableFilters={activeTableFilters}
                        onFilterSubmit={handleTableFilterSubmit}
                        onSingleProjectSelect={selectSingleProject}
                        onRemoveFilter={removeFilter}
                    />
                ) : (
                    <ProjectDataCard projects={data} progress={progress} />
                )}
            </Card.Body>
        </Container >
    );
};

export default Project;
